import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyId } from './useCompanyId';
import { findAccountIdByEmail } from '../services/firestoreService';
import { fetchDepartmentCalls, countCompletedCalls } from '../services/departmentCallsService';

/** Strategy calls for one department (booked + completed, with summaries/recordings). */
export const useDepartmentCalls = (department) => {
  const { currentUser } = useAuth();
  const { companyId } = useCompanyId();
  const [accountId, setAccountId] = useState(null);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      if (companyId) {
        if (active) setAccountId(companyId);
        return;
      }
      if (!currentUser?.email) {
        if (active) setAccountId(null);
        return;
      }
      try {
        const id = await findAccountIdByEmail(currentUser.email, 'Clients');
        if (active) setAccountId(id || null);
      } catch {
        if (active) setAccountId(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [companyId, currentUser?.email]);

  const refresh = useCallback(async () => {
    if (!accountId || !department) {
      setCalls([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const rows = await fetchDepartmentCalls(accountId, department);
      setCalls(rows);
    } catch (e) {
      console.error('Error loading department calls:', e);
      setCalls([]);
    } finally {
      setLoading(false);
    }
  }, [accountId, department]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    accountId,
    calls,
    meetingsCompleted: countCompletedCalls(calls),
    loading,
    refresh,
  };
};
